import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { toneColor, type Tone } from './tone';

export type Slice = {
  label: string;
  value: number;
  tone?: Tone;
  /** a CSS colour or variable, for categories that are not good/bad (e.g. freight modes) */ color?: string;
};
const colorOf = (s: Slice) => s.color ?? toneColor(s.tone ?? 'neutral');

const R = 52;
const C = 2 * Math.PI * R;

/** A ring split into slices, with the total in the middle and a legend that spells out every number (colour is never the only signal) */
type DonutProps = {
  slices: Slice[];
  centerLabel: string;
  /** what to write in the middle instead of the total, e.g. "JOD 1,133" */
  centerValue?: string;
  /** how a slice is written in the legend; the default is "12 (86%)" */
  valueText?: (slice: Slice, percent: number) => string;
};

export function DonutChart({ slices, centerLabel, centerValue, valueText }: DonutProps) {
  const total = slices.reduce((n, s) => n + s.value, 0);
  let offset = 0;
  return (
    <Stack direction="row" spacing={2.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 2 }}>
      <Box sx={{ position: 'relative', width: 150, height: 150, flexShrink: 0 }}>
        <svg
          viewBox="0 0 140 140"
          width="150"
          height="150"
          role="img"
          aria-label={slices.map((s) => `${s.label} ${s.value}`).join(', ')}
        >
          <circle cx="70" cy="70" r={R} fill="none" stroke="var(--mui-palette-divider)" strokeWidth="18" />
          {total > 0 &&
            slices
              .filter((s) => s.value > 0)
              .map((s) => {
                const len = (s.value / total) * C;
                const dash = Math.max(len - 2, 0.5); // a 2px gap between slices
                const el = (
                  <Tooltip
                    key={s.label}
                    title={`${s.label}: ${s.value} (${Math.round((s.value / total) * 100)}%)`}
                    followCursor
                  >
                    <circle
                      cx="70"
                      cy="70"
                      r={R}
                      fill="none"
                      stroke={colorOf(s)}
                      strokeWidth="18"
                      strokeDasharray={`${dash} ${C - dash}`}
                      strokeDashoffset={-offset}
                      transform="rotate(-90 70 70)"
                    />
                  </Tooltip>
                );
                offset += len;
                return el;
              })}
        </svg>
        <Stack sx={{ position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            sx={{ fontWeight: 750, fontSize: centerValue && centerValue.length > 6 ? 15 : 24, lineHeight: 1 }}
          >
            {centerValue ?? total}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {centerLabel}
          </Typography>
        </Stack>
      </Box>
      <Stack spacing={0.75} sx={{ flex: 1, minWidth: 160 }}>
        {slices.map((s) => (
          <Stack key={s.label} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorOf(s), flexShrink: 0 }} />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {s.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {(valueText ?? ((x, p) => `${x.value} (${p}%)`))(s, total ? Math.round((s.value / total) * 100) : 0)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}
